import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getLockerZones,
    createLockerZone,
    updateLockerZone,
    deleteLockerZone,
    getLockerStatus,
    getLockerHistory,
    getLockerCandidates,
    assignLocker,
    releaseLocker,
    releaseExpiredLockers,
    getLockerSlot,
    setLockerSlot,
} from './api';
import { lockerKeys } from './keys';
import { facilitySubscriptionKeys } from '../facilitySubscription/keys';
import { publicMemberKeys } from '../publicMember/keys';

export const invalidateLockerRelatedQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
    queryClient.invalidateQueries({ queryKey: lockerKeys.all });
    queryClient.invalidateQueries({ queryKey: facilitySubscriptionKeys.all });
    queryClient.invalidateQueries({ queryKey: publicMemberKeys.all });
};

// ========================
// 1. 구역 (Locker Zones)
// ========================

export const useLockerZones = ({ centerId, enabled = true }: { centerId: string; enabled?: boolean }) => {
    return useQuery({
        queryKey: lockerKeys.zones(centerId),
        queryFn: () => getLockerZones({ centerId }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data?.results || [],
    });
};

export const useCreateLockerZone = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: createLockerZone,
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: lockerKeys.zones(v.centerId) });
        },
    });
};

export const useUpdateLockerZone = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: updateLockerZone,
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: lockerKeys.zones(v.centerId) });
        },
    });
};

export const useDeleteLockerZone = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: deleteLockerZone,
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: lockerKeys.zones(v.centerId) });
        },
    });
};

// ========================
// 2. 관리 내역 (배정 현황 / 회수 기록)
// ========================

export const useLockerStatus = ({ centerId, zoneId, enabled = true }: { centerId: string; zoneId: string; enabled?: boolean }) => {
    return useQuery({
        queryKey: lockerKeys.status(centerId, zoneId),
        queryFn: () => getLockerStatus({ centerId, zoneId }),
        enabled: enabled && !!centerId && !!zoneId,
        select: (data) => data?.data,
    });
};

export const useLockerHistory = ({
    centerId,
    zoneId,
    from,
    to,
    page = 1,
    limit = 20,
    enabled = true,
}: {
    centerId: string;
    zoneId?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
    enabled?: boolean;
}) => {
    return useQuery({
        queryKey: lockerKeys.history(centerId, { zoneId, from, to, page, limit }),
        queryFn: () => getLockerHistory({ centerId, zoneId, from, to, page, limit }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data,
    });
};

export const useLockerCandidates = ({
    centerId,
    search,
    page = 1,
    limit = 50,
    enabled = true,
}: {
    centerId: string;
    search?: string;
    page?: number;
    limit?: number;
    enabled?: boolean;
}) => {
    return useQuery({
        queryKey: lockerKeys.candidates(centerId, { search, page, limit }),
        queryFn: () => getLockerCandidates({ centerId, search, page, limit }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data,
    });
};

// ========================
// 3. 배정 및 회수 액션
// ========================

export const useAssignLocker = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: assignLocker,
        onSuccess: () => {
            invalidateLockerRelatedQueries(qc);
        },
    });
};

export const useReleaseLocker = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: releaseLocker,
        onSuccess: () => {
            invalidateLockerRelatedQueries(qc);
        },
    });
};

export const useReleaseExpiredLockers = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: releaseExpiredLockers,
        onSuccess: () => {
            invalidateLockerRelatedQueries(qc);
        },
    });
};

// ========================
// 4. 칸별 고정 비밀번호 (locker slot)
// ========================

export const useLockerSlot = ({
    centerId,
    zoneId,
    lockerNumber,
    enabled = true,
}: {
    centerId: string;
    zoneId: string;
    lockerNumber: number | string | null;
    enabled?: boolean;
}) => {
    return useQuery({
        queryKey: lockerKeys.slot(centerId, zoneId, lockerNumber ?? ''),
        queryFn: () => getLockerSlot({ centerId, zoneId, lockerNumber: lockerNumber as number | string }),
        enabled: enabled && !!centerId && !!zoneId && lockerNumber !== null && lockerNumber !== undefined,
        select: (data) => data?.data,
    });
};

/**
 * 고정 비밀번호 설정/해제.
 *
 * `autoInvalidate: false` 는 **일괄 입력 모드 전용**이다. 셀마다 invalidate 하면 500칸 응답을
 * 그만큼 다시 받아 오면서 입력 중인 값이 덮이고 화면이 흔들린다. 일괄 모드는 모드를 끝낼 때
 * 한 번만 invalidate 한다.
 */
export const useSetLockerSlot = ({ autoInvalidate = true }: { autoInvalidate?: boolean } = {}) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: setLockerSlot,
        onSuccess: (_, v) => {
            if (!autoInvalidate) return;
            // 고정값이 바뀌면 그 칸을 쓰는 이용권의 effective 값도 바뀌므로 그리드까지 함께 버린다.
            // unifyExisting 이 개별값을 지웠다면 회원·이용권 화면도 옛 값을 들고 있다.
            qc.invalidateQueries({ queryKey: lockerKeys.slot(v.centerId, v.zoneId, v.lockerNumber) });
            invalidateLockerRelatedQueries(qc);
        },
    });
};
